import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Share2, Copy, Check, Link, Clock, Hash } from 'lucide-react'
import { shares, type ShareResponse, type Track, type TrackWithDetails } from '../api/client'

interface ShareTrackModalProps {
  track: Track | TrackWithDetails
  isOpen: boolean
  onClose: () => void
}

export default function ShareTrackModal({ track, isOpen, onClose }: ShareTrackModalProps) {
  const [shareResult, setShareResult] = useState<ShareResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [expiresInHours, setExpiresInHours] = useState<number | ''>('')
  const [maxPlays, setMaxPlays] = useState<number | ''>('')

  const createShareMutation = useMutation({
    mutationFn: () => shares.create(track.id, {
      expires_in_hours: expiresInHours || null,
      max_plays: maxPlays || null,
    }),
    onSuccess: (data) => {
      setShareResult(data)
    },
  })

  const handleCreateShare = () => {
    createShareMutation.mutate()
  }

  const getFullShareUrl = () => {
    if (!shareResult) return ''
    // Use api subdomain for Discord/social media OpenGraph support
    // The API server serves the OpenGraph HTML at /s/:token
    const apiOrigin = window.location.origin.replace('app.', 'api.')
    return `${apiOrigin}/s/${shareResult.token}`
  }

  const handleCopy = async () => {
    const url = getFullShareUrl()
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = () => {
    setShareResult(null)
    setExpiresInHours('')
    setMaxPlays('')
    createShareMutation.reset()
    onClose()
  }

  if (!isOpen) return null

  const trackTitle = (track as any).title || (track as any).name || 'Unknown Track'
  const artistName = (track as any).artist_name || 'Unknown Artist'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleClose}>
      <div
        className="bg-dark-800 rounded-lg w-full max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <div className="flex items-center gap-3">
            <Share2 size={20} className="text-primary-400" />
            <h2 className="text-lg font-semibold">Share Track</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-dark-700 text-dark-400 hover:text-dark-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Track info */}
          <div className="mb-4 p-3 bg-dark-700/50 rounded-lg">
            <p className="font-medium truncate">{trackTitle}</p>
            <p className="text-sm text-dark-400 truncate">{artistName}</p>
          </div>

          {!shareResult ? (
            /* Share options */
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-300 mb-2">
                  <Clock size={14} />
                  Expires after (hours)
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="Never expires"
                  value={expiresInHours}
                  onChange={(e) => setExpiresInHours(e.target.value ? parseInt(e.target.value) : '')}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-dark-300 mb-2">
                  <Hash size={14} />
                  Maximum plays
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={maxPlays}
                  onChange={(e) => setMaxPlays(e.target.value ? parseInt(e.target.value) : '')}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                />
              </div>

              {createShareMutation.isError && (
                <p className="text-red-400 text-sm">
                  {createShareMutation.error instanceof Error
                    ? createShareMutation.error.message
                    : 'Failed to create share link'}
                </p>
              )}

              <button
                onClick={handleCreateShare}
                disabled={createShareMutation.isPending}
                className="w-full py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-dark-600 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {createShareMutation.isPending ? (
                  'Creating...'
                ) : (
                  <>
                    <Link size={16} />
                    Create Share Link
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Share link result */
            <div className="space-y-4">
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-sm font-medium mb-1">Share link created!</p>
                <p className="text-dark-300 text-xs">
                  {shareResult.expires_at
                    ? `Expires: ${new Date(shareResult.expires_at).toLocaleString()}`
                    : 'Never expires'}
                  {shareResult.max_plays && ` · Max ${shareResult.max_plays} plays`}
                </p>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={getFullShareUrl()}
                  className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={() => setShareResult(null)}
                className="w-full py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors"
              >
                Create another link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
